/**
 * Copyright (c) 2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { hashFnv32a } from '../../../mol-data/util';
import { LocationIterator } from '../../../mol-geo/util/location-iterator';
import { calculateTransformBoundingSphere } from '../../../mol-gl/renderable/util';
import { Sphere3D } from '../../../mol-math/geometry';
import { Vec2, Vec4, Vec3 } from '../../../mol-math/linear-algebra';
import { ValueCell } from '../../../mol-util';
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { BaseGeometry } from '../base';
import { createColors } from '../color-data';
import { createMarkers } from '../marker-data';
import { createEmptyOverpaint } from '../overpaint-data';
import { createEmptyTransparency } from '../transparency-data';
import { fillSerial } from '../../../mol-util/array';
import { createEmptyClipping } from '../clipping-data';
import { NullLocation } from '../../../mol-model/location';
import { QuadPositions } from '../../../mol-gl/compute/util';
var QuadIndices = new Uint32Array([0, 1, 2, 1, 3, 2]);
var QuadUvs = new Float32Array([0, 1, 0, 0, 1, 1, 1, 0]);
export var InterpolationTypes = {
  'nearest': 'Nearest',
  'catmulrom': 'Catmulrom (Cubic)',
  'mitchell': 'Mitchell (Cubic)',
  'bspline': 'B-Spline (Cubic)'
};
export var InterpolationTypeNames = Object.keys(InterpolationTypes);
export { Image };
var Image;

(function (Image) {
  function create(imageTexture, corners, groupTexture, image) {
    return image ? update(imageTexture, corners, groupTexture, image) : fromData(imageTexture, corners, groupTexture);
  }

  Image.create = create;

  function hashCode(image) {
    return hashFnv32a([image.cornerBuffer.ref.version]);
  }

  function fromData(imageTexture, corners, groupTexture) {
    var boundingSphere = Sphere3D();
    var currentHash = -1;
    var width = imageTexture.width;
    var height = imageTexture.height;
    var image = {
      kind: 'image',
      imageTexture: ValueCell.create(imageTexture),
      imageTextureDim: ValueCell.create(Vec2.create(width, height)),
      cornerBuffer: ValueCell.create(corners),
      groupTexture: ValueCell.create(groupTexture),

      get boundingSphere() {
        var newHash = hashCode(image);

        if (newHash !== currentHash) {
          var b = getBoundingSphere(image.cornerBuffer.ref.value);
          Sphere3D.copy(boundingSphere, b);
          currentHash = newHash;
        }

        return boundingSphere;
      }

    };
    return image;
  }

  function update(imageTexture, corners, groupTexture, image) {
    var width = imageTexture.width;
    var height = imageTexture.height;
    ValueCell.update(image.imageTexture, imageTexture);
    ValueCell.update(image.imageTextureDim, Vec2.set(image.imageTextureDim.ref.value, width, height));
    ValueCell.update(image.cornerBuffer, corners);
    ValueCell.update(image.groupTexture, groupTexture);
    return image;
  }

  function createEmpty(image) {
    return {}; // TODO
  }

  Image.createEmpty = createEmpty;
  Image.Params = __assign(__assign({}, BaseGeometry.Params), {
    interpolation: PD.Select('bspline', PD.objectToOptions(InterpolationTypes))
  });
  Image.Utils = {
    Params: Image.Params,
    createEmpty: createEmpty,
    createValues: createValues,
    createValuesSimple: createValuesSimple,
    updateValues: updateValues,
    updateBoundingSphere: updateBoundingSphere,
    createRenderableState: createRenderableState,
    updateRenderableState: updateRenderableState,
    createPositionIterator: function () {
      return LocationIterator(1, 1, 1, function () {
        return NullLocation;
      });
    }
  };

  function createValues(image, transform, locationIt, theme, props) {
    var instanceCount = locationIt.instanceCount,
        groupCount = locationIt.groupCount;
    var positionIt = Image.Utils.createPositionIterator(image, transform);
    var color = createColors(locationIt, positionIt, theme.color);
    var marker = createMarkers(instanceCount * groupCount);
    var overpaint = createEmptyOverpaint();
    var transparency = createEmptyTransparency();
    var clipping = createEmptyClipping();
    var counts = {
      drawCount: QuadIndices.length,
      vertexCount: QuadPositions.length / 3,
      groupCount: groupCount,
      instanceCount: instanceCount
    };
    var invariantBoundingSphere = Sphere3D.clone(image.boundingSphere);
    var boundingSphere = calculateTransformBoundingSphere(invariantBoundingSphere, transform.aTransform.ref.value, instanceCount);
    return __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, color), marker), overpaint), transparency), clipping), transform), BaseGeometry.createValues(props, counts)), {
      aPosition: image.cornerBuffer,
      aUv: ValueCell.create(QuadUvs),
      elements: ValueCell.create(QuadIndices),
      // aGroup is used as a vertex index here, group id is in tGroupTex
      aGroup: ValueCell.create(fillSerial(new Float32Array(4))),
      boundingSphere: ValueCell.create(boundingSphere),
      invariantBoundingSphere: ValueCell.create(invariantBoundingSphere),
      uInvariantBoundingSphere: ValueCell.create(Vec4.ofSphere(invariantBoundingSphere)),
      dInterpolation: ValueCell.create(props.interpolation),
      uImageTexDim: image.imageTextureDim,
      tImageTex: image.imageTexture,
      tGroupTex: image.groupTexture
    });
  }

  function createValuesSimple(image, props, colorValue, sizeValue, transform) {
    var s = BaseGeometry.createSimple(colorValue, sizeValue, transform);

    var p = __assign(__assign({}, PD.getDefaultValues(Image.Params)), props);

    return createValues(image, s.transform, s.locationIterator, s.theme, p);
  }

  function updateValues(values, props) {
    BaseGeometry.updateValues(values, props);
    ValueCell.updateIfChanged(values.dInterpolation, props.interpolation);
  }

  function updateBoundingSphere(values, image) {
    var invariantBoundingSphere = Sphere3D.clone(image.boundingSphere);
    var boundingSphere = calculateTransformBoundingSphere(invariantBoundingSphere, values.aTransform.ref.value, values.instanceCount.ref.value);

    if (!Sphere3D.equals(boundingSphere, values.boundingSphere.ref.value)) {
      ValueCell.update(values.boundingSphere, boundingSphere);
    }

    if (!Sphere3D.equals(invariantBoundingSphere, values.invariantBoundingSphere.ref.value)) {
      ValueCell.update(values.invariantBoundingSphere, invariantBoundingSphere);
      ValueCell.update(values.uInvariantBoundingSphere, Vec4.fromSphere(values.uInvariantBoundingSphere.ref.value, invariantBoundingSphere));
    }
  }

  function createRenderableState(props) {
    var state = BaseGeometry.createRenderableState(props);
    state.opaque = false;
    return state;
  }

  function updateRenderableState(state, props) {
    BaseGeometry.updateRenderableState(state, props);
    state.opaque = false;
  }
})(Image || (Image = {})); //


function getBoundingSphere(corners) {
  var center = Vec3();
  var extrema = [];

  for (var i = 0, il = corners.length; i < il; i += 3) {
    var e = Vec3.fromArray(Vec3(), corners, i);
    extrema.push(e);
    Vec3.add(center, center, e);
  }

  Vec3.scale(center, center, 1 / (corners.length / 3));
  var radius = 0;

  for (var _i = 0, extrema_1 = extrema; _i < extrema_1.length; _i++) {
    var e = extrema_1[_i];
    var d = Vec3.distance(center, e);
    if (d > radius) radius = d;
  }

  var sphere = Sphere3D.create(center, radius);
  Sphere3D.setExtrema(sphere, extrema);
  return sphere;
}